import React from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
}

// Modern theme styles - matches original ui exactly
const modernVariantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-primary-500 hover:bg-primary-600 text-white shadow-lg shadow-primary-500/25',
  secondary: 'bg-surface-600 hover:bg-surface-500 text-white border border-surface-500',
  ghost: 'bg-transparent hover:bg-surface-700 text-gray-300',
  danger: 'bg-danger-500 hover:bg-danger-600 text-white',
};

// Command theme styles
const commandVariantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-primary-500 text-surface-900 border-primary-400 hover:bg-primary-400 hover:shadow-glow active:bg-primary-600',
  secondary: 'bg-transparent text-primary-400 border-primary-500/50 hover:bg-primary-500/10 hover:border-primary-500 hover:shadow-glow-sm active:bg-primary-500/20',
  ghost: 'bg-transparent text-text-secondary border-transparent hover:bg-surface-700 hover:text-text-primary active:bg-surface-600',
  danger: 'bg-danger-500 text-text-primary border-danger-500 hover:shadow-glow-danger active:opacity-90',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

export function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const { themeName } = useTheme();
  const isCommand = themeName === 'command';

  const variantStyles = isCommand ? commandVariantStyles : modernVariantStyles;

  return (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 font-medium rounded-lg',
        'transition-all duration-150 ease-out',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-800',
        isCommand && 'font-body font-semibold uppercase tracking-wider border rounded',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      ) : (
        leftIcon
      )}
      {children}
      {!isLoading && rightIcon}
    </button>
  );
}
